import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetOrganizationsForMeasuresQuery,
  useGetOrganizationMeasureAccessQuery,
  useUpdateOrganizationMeasureAccessMutation,
  useBulkUpdateOrganizationMeasureAccessMutation,
  OrganizationMeasureItem,
} from "../../app/services/meseeknaApiV2"
import { FiSearch, FiLock, FiUnlock } from "react-icons/fi"
import { HiOutlineOfficeBuilding } from "react-icons/hi"
import { MdOutlineInsights, MdOutlineCategory } from "react-icons/md"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const LockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiLock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UnlockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUnlock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const InsightsIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineInsights {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CategoryIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineCategory {...props} />

const MeasureCard: React.FC<{
  measure: OrganizationMeasureItem
  organizationId: number
  onToggle: (measureId: number, grantAccess: boolean) => void
  isUpdating: boolean
}> = ({ measure, onToggle, isUpdating }) => {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border p-4 transition-all ${
        measure.hasAccess
          ? "border-green-200 bg-green-50/30"
          : "border-gray-200"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900">{measure.measureName}</h4>
            {measure.hasAccess ? (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                <UnlockIcon size={10} />
                Unlocked
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                <LockIcon size={10} />
                Locked
              </span>
            )}
          </div>
          {measure.measureDescription && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {measure.measureDescription}
            </p>
          )}
        </div>
        <button
          onClick={() => onToggle(measure.id, !measure.hasAccess)}
          disabled={isUpdating}
          className={`ml-4 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            measure.hasAccess
              ? "bg-red-100 text-red-700 hover:bg-red-200"
              : "bg-green-100 text-green-700 hover:bg-green-200"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isUpdating ? (
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            </span>
          ) : measure.hasAccess ? (
            <span className="flex items-center gap-1">
              <LockIcon size={14} />
              Revoke
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <UnlockIcon size={14} />
              Grant
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

const OrgMeasuresPage: React.FC = () => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [updatingMeasureId, setUpdatingMeasureId] = useState<number | null>(null)

  const { data: orgsData, isLoading: orgsLoading } = useGetOrganizationsForMeasuresQuery()
  const { data: measureAccessData, isLoading: measuresLoading, refetch } = useGetOrganizationMeasureAccessQuery(
    selectedOrgId as number,
    { skip: !selectedOrgId }
  )
  const [updateMeasureAccess] = useUpdateOrganizationMeasureAccessMutation()
  const [bulkUpdateMeasureAccess, { isLoading: isBulkUpdating }] = useBulkUpdateOrganizationMeasureAccessMutation()

  const organizations = orgsData?.data || []
  const measureAccess = measureAccessData?.data
  const measures = measureAccess?.measures || []

  const filteredMeasures = useMemo(() => {
    return measures.filter((m) =>
      m.measureName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.measureDescription && m.measureDescription.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  }, [measures, searchQuery])

  const stats = useMemo(() => {
    return {
      total: measures.length,
      unlocked: measures.filter((m) => m.hasAccess).length,
      locked: measures.filter((m) => !m.hasAccess).length,
    }
  }, [measures])

  const handleToggleMeasure = async (measureId: number, grantAccess: boolean) => {
    if (!selectedOrgId) return
    setUpdatingMeasureId(measureId)
    try {
      await updateMeasureAccess({
        organizationId: selectedOrgId,
        measureId,
        grantAccess,
      }).unwrap()
      refetch()
    } catch (error) {
      console.error("Failed to update measure access:", error)
    } finally {
      setUpdatingMeasureId(null)
    }
  }

  const handleBulkAction = async (grantAccess: boolean) => {
    if (!selectedOrgId) return
    const measureIds = grantAccess
      ? measures.filter((m) => !m.hasAccess).map((m) => m.id)
      : measures.filter((m) => m.hasAccess).map((m) => m.id)

    if (measureIds.length === 0) return

    try {
      await bulkUpdateMeasureAccess({
        organizationId: selectedOrgId,
        measureIds,
        grantAccess,
      }).unwrap()
      refetch()
    } catch (error) {
      console.error("Failed to bulk update measure access:", error)
    }
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <InsightsIcon size={24} className="text-violet-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Organization Measures
                  </h1>
                  <p className="text-gray-500">
                    Manage measure access for organizations - unlock or lock the 30 measures
                  </p>
                </div>
              </div>
            </div>

            {/* Organization Selector */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-4">
                <BuildingIcon size={20} className="text-gray-400" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Organization
                  </label>
                  <select
                    value={selectedOrgId || ""}
                    onChange={(e) => setSelectedOrgId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    disabled={orgsLoading}
                  >
                    <option value="">Choose an organization...</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.organizationName} ({org.measureCount} measures unlocked)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Content Area */}
            {!selectedOrgId ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <BuildingIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select an Organization
                </h3>
                <p className="text-gray-500">
                  Choose an organization above to view and manage their measure access.
                </p>
              </div>
            ) : measuresLoading ? (
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="h-16 bg-gray-200 rounded"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
                      <div className="h-5 bg-gray-200 rounded w-2/3 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Stats & Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {measureAccess?.organizationName} - Measure Access
                    </h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleBulkAction(true)}
                        disabled={isBulkUpdating || stats.locked === 0}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                      >
                        <UnlockIcon size={16} />
                        Unlock All
                      </button>
                      <button
                        onClick={() => handleBulkAction(false)}
                        disabled={isBulkUpdating || stats.unlocked === 0}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                      >
                        <LockIcon size={16} />
                        Lock All
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                      <p className="text-sm text-gray-500">Total Measures</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{stats.unlocked}</p>
                      <p className="text-sm text-green-600">Unlocked</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-600">{stats.locked}</p>
                      <p className="text-sm text-gray-500">Locked</p>
                    </div>
                  </div>
                </div>

                {/* Search */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                  <div className="relative">
                    <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search measures..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    />
                  </div>
                </div>

                {/* Measures Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredMeasures.map((measure) => (
                    <MeasureCard
                      key={measure.id}
                      measure={measure}
                      organizationId={selectedOrgId}
                      onToggle={handleToggleMeasure}
                      isUpdating={updatingMeasureId === measure.id}
                    />
                  ))}
                </div>

                {filteredMeasures.length === 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <CategoryIcon size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No Measures Found
                    </h3>
                    <p className="text-gray-500">
                      No measures match your search criteria.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default OrgMeasuresPage
